<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import parseISO from 'date-fns/parseISO';
import format from 'date-fns/format';
import Spinner from 'shared/components/Spinner.vue';
import BarChart from 'shared/components/charts/NewBarChart.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const chartData = computed(() => {
  const labels = props.data.map(entry => {
    if (!entry.timestamp) {
      return t('DATA_COLLECTION_REPORTS.TREND.UNKNOWN');
    }
    return format(parseISO(entry.timestamp), 'dd MMM');
  });

  return {
    labels,
    datasets: [
      {
        label: t('DATA_COLLECTION_REPORTS.TREND.FIELDS'),
        backgroundColor: '#1f93ff',
        borderRadius: 6,
        data: props.data.map(entry => entry.collectedFields || 0),
      },
      {
        label: t('DATA_COLLECTION_REPORTS.TREND.CUSTOMERS'),
        backgroundColor: '#10b981',
        borderRadius: 6,
        data: props.data.map(entry => entry.uniqueCustomers || 0),
      },
    ],
  };
});

const hasData = computed(() => !!props.data.length);
</script>

<template>
  <div class="rounded-2xl border border-n-weak bg-white/80 p-4 shadow-sm dark:border-white/10 dark:bg-slate-900/30">
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-base font-semibold text-n-slate-12 dark:text-white">
          {{ $t('DATA_COLLECTION_REPORTS.TREND.TITLE') }}
        </h3>
        <p class="text-sm text-n-gray-6 dark:text-n-gray-4">
          {{ $t('DATA_COLLECTION_REPORTS.TREND.SUBTITLE') }}
        </p>
      </div>
    </div>
    <div class="mt-4 min-h-[240px]">
      <div
        v-if="isLoading"
        class="flex h-full w-full items-center justify-center"
      >
        <Spinner size="18" />
      </div>
      <div
        v-else-if="!hasData"
        class="flex h-full w-full flex-col items-center justify-center text-center text-sm text-n-gray-5 dark:text-n-gray-3"
      >
        {{ $t('DATA_COLLECTION_REPORTS.TREND.EMPTY') }}
      </div>
      <BarChart v-else :collection="chartData" />
    </div>
  </div>
</template>
